import { useState, ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button, ButtonVariant, ButtonSize } from './Button';
import { useToast } from './Toast';

export interface CopyButtonProps {
  value: string;
  /** Tekst na buttonie. Gdy brak - icon-only. */
  label?: ReactNode;
  size?: ButtonSize;
  variant?: Extract<ButtonVariant, 'ghost' | 'secondary' | 'icon'>;
  /** Pokaż toast po skopiowaniu. Default: true. */
  showToast?: boolean;
  /** Czas w ms pokazania ikony Check po skopiowaniu. Default 2000. */
  feedbackMs?: number;
  className?: string;
}

/**
 * Button który kopiuje value do schowka. Po kliknięciu ikona Copy zmienia
 * się na Check przez 2s (feedback). Opcjonalny toast.
 */
export function CopyButton({
  value,
  label,
  size = 'sm',
  variant = 'ghost',
  showToast = true,
  feedbackMs = 2000,
  className,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (showToast) toast.success('Skopiowano do schowka');
      setTimeout(() => setCopied(false), feedbackMs);
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  const Icon = copied ? Check : Copy;

  return (
    <Button
      variant={variant}
      size={size}
      leftIcon={<Icon />}
      onClick={handleCopy}
      className={className}
      aria-label={label ? undefined : 'Skopiuj do schowka'}
    >
      {label}
    </Button>
  );
}
